import type {Priority, Task} from '../../types'
import {parseLocalDate, isTaskOverdue} from '../../utils/helpers'

export type DueDateRange = 'all' | 'overdue' | 'today' | 'this_week' | 'this_month' | 'no_date'
export type AssignmentFilter = 'all' | 'mine' | 'unassigned'
export type CardSort = 'default' | 'priority' | 'dueDate' | 'createdAt' | 'title'

export interface SharedTaskFilters {
  search: string
  status: string[]
  priority: Priority[]
  assigneeIds: string[]
  labelIds: string[]
  dueDateRange: DueDateRange
  assignment: AssignmentFilter
  pendingOnly?: boolean
}

export const dueDateOptions: {value: DueDateRange; label: string}[] = [
  {value: 'all', label: 'Todos os prazos'},
  {value: 'overdue', label: 'Atrasadas'},
  {value: 'today', label: 'Vencem hoje'},
  {value: 'this_week', label: 'Até domingo'},
  {value: 'this_month', label: 'Este mês'},
  {value: 'no_date', label: 'Sem prazo'},
]

export function matchesDueDateRange(task: Task, range: DueDateRange, now = new Date()): boolean {
  if (range === 'all') return true
  if (range === 'no_date') return !task.dueDate
  if (!task.dueDate) return false
  if (range === 'overdue') return isTaskOverdue(task, now)
  const due = parseLocalDate(task.dueDate)
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate())
  if (Number.isNaN(dueDay.getTime())) return false
  if (range === 'today') return dueDay.getTime() === today.getTime()
  if (range === 'this_month')
    return dueDay.getMonth() === today.getMonth() && dueDay.getFullYear() === today.getFullYear()
  const weekEnd = new Date(today)
  weekEnd.setDate(today.getDate() + ((7 - today.getDay()) % 7))
  return dueDay >= today && dueDay <= weekEnd
}

const normalizeSearch = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')

export function matchesTaskFilters(
  task: Task,
  filters: SharedTaskFilters,
  currentUserId?: string
): boolean {
  if (filters.pendingOnly && task.status === 'done') return false
  const query = normalizeSearch(filters.search.trim())
  const text = normalizeSearch(
    `${task.title} ${task.description.replace(/<[^>]*>/g, ' ')} ${task.id}`
  )
  if (query && !text.includes(query)) return false
  if (filters.status.length && !filters.status.includes(task.status)) return false
  if (filters.priority.length && !filters.priority.includes(task.priority)) return false
  if (
    filters.assigneeIds.length &&
    !filters.assigneeIds.some((id) => task.assigneeIds.includes(id))
  )
    return false
  if (filters.labelIds.length && !filters.labelIds.some((id) => task.labelIds.includes(id)))
    return false
  if (filters.assignment === 'unassigned' && task.assigneeIds.length) return false
  if (
    filters.assignment === 'mine' &&
    (!currentUserId || !task.assigneeIds.includes(currentUserId))
  )
    return false
  return matchesDueDateRange(task, filters.dueDateRange)
}

export function sortBoardTasks(tasks: Task[], sort: CardSort): Task[] {
  const priority: Record<Priority, number> = {critical: 0, high: 1, medium: 2, low: 3}
  return [...tasks].sort((a, b) => {
    if (sort === 'priority') return priority[a.priority] - priority[b.priority]
    if (sort === 'dueDate') return (a.dueDate || '9999').localeCompare(b.dueDate || '9999')
    if (sort === 'createdAt') return b.createdAt.localeCompare(a.createdAt)
    if (sort === 'title') return a.title.localeCompare(b.title, 'pt-BR')
    return 0
  })
}
